<template>
  <BaseLoaderModal v-if="isLoaderModalOpen" />

  <PurchaseViaSms
    v-if="isPurchaseViaSmsModalOpen"
    :bundle-type="bundleType"
    :quantity-text="quantityText"
    :price-text="priceText"
  />

  <PurchaseViaWeb
    v-else-if="isPurchaseViaWebModalOpen"
    :bundle-id="bundleId"
    :bundle-type="bundleType"
    :quantity-text="quantityText"
    :price-text="priceText"
    @purchase:viaWeb="handlePurchaseViaWeb"
  />

  <PurchaseViaPin
    v-else-if="isPurchaseViaPinModalOpen"
    :bundle-id="bundleId"
    :bundle-type="bundleType"
    :quantity-text="quantityText"
    :price-text="priceText"
    @purchase:viaPin="handlePurchaseViaPin"
  />

  <PurchaseSuccessModal
    v-else-if="isPurchaseSuccessModalOpen"
    :quantity-text="quantityText"
    @start:newGame="handleStartNewGameFromSuccessModal"
  />

  <PurchaseErrorModal v-else-if="isPurchaseErrorModalOpen" />

  <PurchaseErrorOperatorModal v-else-if="isPurchaseErrorOperatorModalOpen" />

  <PurchaseErrorPinModal v-else-if="isPurchaseErrorPinModalOpen" />
</template>

<script setup>
import { computed, getCurrentInstance, onMounted } from 'vue';
import BaseLoaderModal from '@/components/modal/loader/BaseLoaderModal.vue';
import PurchaseViaSms from '@/views/store/components/modal/PurchaseViaSmsModal.vue';
import PurchaseViaWeb from '@/views/store/components/modal/PurchaseViaWebModal.vue';
import PurchaseViaPin from '@/views/store/components/modal/PurchaseViaPinModal.vue';
import PurchaseSuccessModal from '@/views/store/components/modal/PurchaseSuccessModal.vue';
import PurchaseErrorModal from '@/views/store/components/modal/PurchaseErrorModal.vue';
import PurchaseErrorOperatorModal
  from '@/views/store/components/modal/PurchaseErrorOperatorModal.vue';
import PurchaseErrorPinModal from '@/views/store/components/modal/PurchaseErrorPinModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useDeviceHook from '@/hooks/useDevice.js';
import useModalHook from '@/hooks/useModal.js';
import { apiRequestPinForBundlePurchase } from '@/services/bundle.js';
import { bundleTypeEnum } from '@/constants/bundle.js';
import { apiResponseTypeEnum } from '@/constants/api.js';

const emits = defineEmits([
  'purchase:resolved',
  'purchase:startNewGame',
]);

const props = defineProps({
  bundle: {
    type: Object,
    default: null,
  },
});

const { proxy } = getCurrentInstance();

const appSettingsStore = useAppSettingsStore();

const { isMobileDevice, openSmsExternalApp } = useDeviceHook();
const [isLoaderModalOpen, openLoaderModal, closeLoaderModal] = useModalHook();
const [isPurchaseViaSmsModalOpen, openPurchaseViaSmsModal] = useModalHook();
const [isPurchaseViaWebModalOpen, openPurchaseViaWebModal, closePurchaseViaWebModal] = useModalHook();
const [isPurchaseViaPinModalOpen, openPurchaseViaPinModal, closePurchaseViaPinModal] = useModalHook();
const [isPurchaseSuccessModalOpen, openPurchaseSuccessModal, closePurchaseSuccessModal] = useModalHook();
const [isPurchaseErrorModalOpen, openPurchaseErrorModal] = useModalHook();
const [isPurchaseErrorOperatorModalOpen, openPurchaseErrorOperatorModal] = useModalHook();
const [isPurchaseErrorPinModalOpen, openPurchaseErrorPinModal] = useModalHook();

const isBundleValid = computed(() => !!props.bundle && Object.values(bundleTypeEnum).includes(props.bundle.__bundleType));
const bundleId = computed(() => isBundleValid.value ? props.bundle.bundleId : '');
const bundleType = computed(() => isBundleValid.value ? props.bundle.__bundleType : '');
const quantityText = computed(() => {
  if (!isBundleValid.value) { return ''; }

  if (bundleType.value === bundleTypeEnum.GAMES) {
    const numberOfGames = props.bundle.noOfGames;

    return `${ numberOfGames } ${ proxy.$_translate((numberOfGames > 1) ? 'page_store_game_plural' : 'page_store_game') || '' }`;
  } else if (bundleType.value === bundleTypeEnum.JOKERS) {
    const numberOfJokers = props.bundle.jokers;

    return `${ numberOfJokers } ${ proxy.$_translate((numberOfJokers > 1) ? 'page_store_joker_plural' : 'page_store_joker') || '' }`;
  } else {
    return '';
  }
});
const priceText = computed(() => isBundleValid.value ? `${ props.bundle.price } ${ props.bundle.currencyCode }` : '');

const handlePurchaseViaWeb = async (result) => {
  await closePurchaseViaWebModal({ immediate: true });

  if (result.status === 'success') {
    openPurchaseSuccessModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  } else if (result.status === 'error_operator') {
    openPurchaseErrorOperatorModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  } else {
    openPurchaseErrorModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  }
};

const handlePurchaseViaPin = async (result) => {
  await closePurchaseViaPinModal({ immediate: true });

  if (result.status === 'success') {
    openPurchaseSuccessModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  } else if (result.status === 'error_pin') {
    openPurchaseErrorPinModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  } else {
    openPurchaseErrorModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  }
};

const handleStartNewGameFromSuccessModal = async () => {
  await closePurchaseSuccessModal({ immediate: true });

  emits('purchase:startNewGame');
};

const purchaseBundle = async () => {
  if (appSettingsStore.multiOperatorProperties.purchaseViaSMS) {
    if (appSettingsStore.multiOperatorProperties.smsAutomaticResponse && isMobileDevice) {
      openSmsExternalApp('purchase_via_mobile_sms_shortcode', 'purchase_via_mobile_sms_message');

      emits('purchase:resolved');
    } else {
      openPurchaseViaSmsModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
    }
  } else if (appSettingsStore.multiOperatorProperties.pinForBundles) {
    openLoaderModal({ showImmediate: true, hideManually: true });

    const { responseEnum } = await apiRequestPinForBundlePurchase({ payload: { bundleId: bundleId.value } });

    await closeLoaderModal({ immediate: true });

    if (responseEnum === apiResponseTypeEnum.OPERATION_SUCCESS) {
      openPurchaseViaPinModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
    } else {
      openPurchaseErrorPinModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
    }
  } else {
    openPurchaseViaWebModal({ showImmediate: true, afterHideCallback: () => emits('purchase:resolved') });
  }
};

onMounted(purchaseBundle);
</script>
